import React, { useCallback, useEffect, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha' // eslint-disable-line import/no-named-as-default
import { connect } from 'react-redux'
import { NavigateFunction, useLocation, useNavigate } from 'react-router'
import { createSelector, Dispatch } from '@reduxjs/toolkit'

import {
  addNotification,
  removeNotification as removeNotificationAction
} from '../notifications/notifications-actions'
import type { NotificationType } from '../notifications/types'
import { killState as killStateAction } from '../rootReducer'
import { getReCaptchaKey } from '../shared/constants'
import type { GlobalState } from '../types'
import { claimError, enableInputs, fetchClaim as fetchClaimAction } from './claim-actions'
import { ClaimDisplay } from './ClaimDisplay'
import { ClaimForm } from './ClaimForm'

type ClaimProps = {
  addNotification(arg: NotificationType): void
  notifications: NotificationType[]
  enableInputs(): void
  removeNotification(arg: string): void
  fetchClaim(
    claimNumber: string,
    dateOfLoss: string,
    url: string,
    recaptchaToken: string,
    navigate: NavigateFunction
  ): void
  killState(): void
  formValues: {
    values?: {
      claimNumber?: string
      dateOfLoss?: string
    }
  }
  requesting: boolean
  disabled: boolean
}

const ClaimComponent = (props: ClaimProps) => {
  const {
    addNotification,
    disabled,
    enableInputs,
    fetchClaim,
    formValues,
    notifications,
    removeNotification,
    requesting,
    killState
  } = props

  const refRecaptcha = useRef<ReCAPTCHA>(null)
  const refIsFirstRun = useRef(true)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Reset LOB additional attribute
    document.title ='File Uploads - Find Claim'

    if (location && location.state && location.state.claimError) {
      // Session timeout path
      // clear location state to prevent this block from running on refresh.
      window.history.replaceState({}, document.title)

      // Kill State for previously entered information
      killState()
      addNotification({
        name: 'claimError',
        body: location.state.claimError,
        title: 'SECURE SESSION EXPIRED',
        severity: 'warning'
      })
    }
  }, [location, addNotification, killState])

  useEffect(() => {
    if (refRecaptcha?.current && !disabled && !requesting) {
      if (!refIsFirstRun.current) {
        refRecaptcha.current.reset()
      } else {
        refIsFirstRun.current = false
      }
    }
  }, [disabled, requesting])


  useEffect(() => {
    return () => {
      enableInputs()
    }
  }, [enableInputs])

  const sendRequest = useCallback((recaptchaToken: string) => {
    fetchClaim(
      formValues.values?.claimNumber ?? '',
      formValues.values?.dateOfLoss ?? '',
      `${window.travURI}identity`,
      recaptchaToken,
      navigate
    )
  }, [fetchClaim, formValues, navigate])

  // Redux Form will only call method once all form values are valid
  const submit = useCallback((event: React.SyntheticEvent) => {
    // prevent form from making query that refreshes page and prevents
    // recaptcha and saga calls.
    event.preventDefault()
    if (notifications.length > 0) {
      removeNotification('claimError')
    }
    refRecaptcha?.current?.execute()
  }, [notifications, removeNotification])

  const onRecaptchaResolved = useCallback(() => {
    const recaptchaToken = refRecaptcha?.current?.getValue()
    if (recaptchaToken) {
      sendRequest(recaptchaToken)
    } else {
      addNotification({
        name: 'claimError',
        body: 'We are unable to process your request. Please contact your Claim professional.',
        severity: 'error',
        title: 'Claim Error'
      })
    }
  }, [addNotification, sendRequest])

  const captchaKey = getReCaptchaKey(window.location.hostname)

  return (
    <ClaimDisplay>
      <ClaimForm
        handleSubmit={submit}
        requesting={requesting}
        disabled={disabled}
      />
      <ReCAPTCHA
        id='claimFormRecaptcha'
        size='invisible'
        ref={refRecaptcha}
        sitekey={captchaKey}
        onChange={onRecaptchaResolved}
      />
    </ClaimDisplay>
  )
}

const notificationSelector = (state: GlobalState) => state.notifications.notifications

const getNotifications = createSelector(
  notificationSelector,
  (notifications: NotificationType[]) =>
    notifications.filter((n: NotificationType) => n.name === 'claimError')
)

const mapStateToProps = (state: GlobalState) => ({
  errors: state.claim.errors,
  valid: state.claim.valid,
  requesting: state.claim.requesting,
  formValues: state.form.claimForm,
  disabled: state.claim.disableInputs,
  notifications: getNotifications(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchClaim: (
    claim: string,
    dateOfLoss: string,
    redirectTo: string,
    recaptchaToken: string,
    navigate: NavigateFunction
  ) =>
    dispatch(fetchClaimAction(claim, dateOfLoss, redirectTo, recaptchaToken, navigate)),
  killState: () => dispatch(killStateAction()),
  claimError: (error: unknown) => dispatch(claimError(error)),
  enableInputs: () => dispatch(enableInputs()),
  removeNotification: (name: string) => dispatch(removeNotificationAction(name)),
  addNotification: (options: NotificationType) => dispatch(addNotification(options))
})

const Claim = connect(mapStateToProps, mapDispatchToProps)(ClaimComponent)

export {
  Claim,
  ClaimComponent // for testing without HOC
}
